<script lang="ts">
  // A community feed post, Skool-style: avatar + author/meta header divided from
  // the content, an optional title, the body (clamped to a fixed number of rows in
  // summary mode / rendered richly when opened), inline images, and a footer of
  // like + comment counts.
  //
  //   • summary (default): `clamp` > 0 → body flattened to plain text, clamped to N
  //     rows so every feed card is a consistent height; the whole card is one click
  //     target (stretched-link) when `onclick` is set.
  //   • full: `clamp === 0` → body renders via <SkoolContent>, unclamped.
  import Avatar from './Avatar.svelte';
  import Upvote from './Upvote.svelte';
  import SkoolLink from './SkoolLink.svelte';
  import DeletedBadge from './DeletedBadge.svelte';
  import MemberHover from './MemberHover.svelte';
  import Clamp from './Clamp.svelte';
  import SkoolContent from './SkoolContent.svelte';
  import SkoolMedia from './SkoolMedia.svelte';
  import type { Snippet } from 'svelte';
  import { skoolToPlain, extractImages, stripImages } from './skoolContent';
  import { timeAgo, type PostAuthor, type ImageMedia, type VideoMedia } from './member';

  let {
    author = null,
    title = null,
    body = null,
    images = null,
    videos = null,
    skoolUrl = null,
    upvotes = 0,
    commentCount = null,
    createdAt = null,
    category = null,
    pinned = false,
    selected = false,
    clamp = 3,
    mediaSide = false,
    showHandle = false,
    compact = false,
    onReply = null,
    liked = false,
    onLike = null,
    replyActive = false,
    now = Date.now(),
    onclick = null,
    badges = null,
    actions = null,
    deleted = false,
  }: {
    author?: PostAuthor | null;
    title?: string | null;
    body?: string | null;
    /** Stored images (uploads + gifs). Falls back to images parsed from the body. */
    images?: ImageMedia[] | null | undefined;
    /** Stored videos (Skool-native posters + external embeds). */
    videos?: VideoMedia[] | null | undefined;
    /** This post's URL on Skool — the deep-link target for a native video. */
    skoolUrl?: string | null;
    upvotes?: number | null;
    commentCount?: number | null;
    createdAt?: string | null;
    category?: string | null;
    pinned?: boolean;
    selected?: boolean;
    /** Body rows to clamp to in summary mode; 0 → full <SkoolContent> render. */
    clamp?: number;
    /** Summary cards only: render media as a compact square to the RIGHT of the
     *  text (instead of below) so the card height stays governed by the text. */
    mediaSide?: boolean;
    showHandle?: boolean;
    /**
     * Tighten every gap, the title, the body and the avatar for a narrow column.
     *
     * The Helper's side panel is ~340px and ran its own copy of this file purely to
     * hold these seven values. A prop, because density is the caller's fact — the
     * component cannot measure the column it lands in, and a container query would
     * fight the panel, which is narrow at every width.
     */
    compact?: boolean;
    /**
     * Ask to reply to THIS one. Given, a "Reply" affordance shows and calling it is
     * all this card does — the composer itself is mounted once by whoever owns the
     * thread, so there is one reply box on screen and one code path behind it.
     *
     * It used to mount its own `<ReplyComposer>` here, which never worked from a
     * thread: `CommentThread` forwarded the community but not the submit callback, so
     * the box opened with no send button at all — the composer's action row is gated
     * on having somewhere to send.
     */
    onReply?: (() => void) | null;
    /** Already liked on Skool. Renders the thumb filled and inert — there is no
     *  unlike, here or anywhere: see `<Upvote>`. */
    liked?: boolean;
    /** Perform the like. Absent on any surface that cannot write to Skool, which is
     *  the dashboard unless a Helper in the same browser can be asked to do it. */
    onLike?: (() => Promise<void> | void) | null;
    /** True when the one composer is currently open on this card. */
    replyActive?: boolean;
    now?: number;
    onclick?: (() => void) | null;
    /** Optional chip row rendered at the very top of the card (e.g. moderation context). */
    badges?: Snippet | null;
    /** Optional footer actions, rendered right of the Skool link (above the click layer). */
    actions?: Snippet | null;
    /** Gone from Skool. Renders the card tinted and badged rather than hiding it — a
     *  deleted post is still the reason a queue row exists. See `<DeletedBadge>`. */
    deleted?: boolean;
  } = $props();

  const clamped = $derived(clamp > 0);
  const authorName = $derived(author?.name || author?.handle || 'Unknown');
  // Stored media is the source of truth; if none was captured, fall back to images
  // parsed out of the body markdown (harmless when the stored list is present).
  const mediaImages = $derived<ImageMedia[]>(
    images && images.length
      ? images
      : extractImages(body).map((url) => ({ url, width: null, height: null })),
  );
  const hasMedia = $derived(mediaImages.length > 0 || (videos?.length ?? 0) > 0);
  const textBody = $derived(stripImages(body));
  const plain = $derived(clamped ? skoolToPlain(textBody) : '');

  // Inline reply is offered only on a non-clickable (detail/thread) card.
  /** Same rule as `<SkoolComment>`: a deleted post has no id left to file a reply
   *  under, so the control is not offered. The card still clears. */
  const canReply = $derived(!!onReply && !onclick && !deleted);

  /**
   * A CARD IS FOR A ROW IN A LIST, not for the thing you are reading.
   *
   * In a feed this is one of many and the border says where it starts and stops. In a
   * thread it is the subject of the whole screen, and boxing it — then boxing every
   * comment under it, inside another box — was most of why that screen read as
   * cluttered. So the frame appears only where the card is a summary someone clicks.
   */
  const framed = $derived(!!onclick || selected);
  const baseCls = $derived(
    `block w-full text-left ${
      framed
        ? `rounded-box border bg-base-100 ${compact ? 'px-3 py-3' : 'px-4 py-4'}`
        : ''
    }`,
  );
  /**
   * The deleted wash, which has to survive BOTH framings.
   *
   * `framed` is false in a thread — the deck's card is the frame there — so a tint
   * hung off the framed branch would be invisible in the one place this state matters
   * most. This adds its own rounded region either way, and the padding comes with it
   * so the wash has an edge to sit against rather than hugging the glyphs.
   *
   * It stacks UNDER the badge rather than replacing it: a red wash alone could read as
   * an error or a selection, and the badge is what names the state.
   */
  const deletedCls = $derived(
    !deleted
      ? ''
      : `rounded-box bg-error/[0.05] ring-1 ring-inset ring-error/20 ${framed ? '' : compact ? 'px-3 py-3' : 'px-4 py-4'}`,
  );
  const stateCls = $derived(
    !framed
      ? ''
      : selected
        ? 'border-primary/50 bg-primary/[0.05] shadow-sm shadow-primary/5'
        : 'hair shadow-sm shadow-black/[0.04] transition-all hover:border-base-content/20 hover:shadow-md hover:shadow-black/[0.07]',
  );
</script>

{#snippet textBlock()}
  {#if title}
    <h3 class="flex items-start gap-2 font-display {compact ? 'text-base' : 'text-[1.22rem]'} font-bold leading-snug {selected ? 'text-primary' : 'text-base-content'}">
      <span class="mt-[0.5em] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70"></span>
      <span class="redact min-w-0" style={clamped ? 'display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden' : ''}>{title}</span>
    </h3>
  {/if}
  {#if textBody}
    {#if clamped}
      <p
        class="redact break-words whitespace-pre-wrap text-sm leading-relaxed text-base-content/75"
        style="display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:{clamp};overflow:hidden"
      >{plain}</p>
    {:else}
      <Clamp max={compact ? 200 : 300} label="Show the whole post">
        <SkoolContent text={textBody} class="{compact ? 'text-sm' : 'text-[0.95rem]'} leading-relaxed text-base-content/90" />
      </Clamp>
    {/if}
  {/if}
{/snippet}

{#snippet inner()}
  <div class="flex flex-col {compact ? 'gap-3' : 'gap-4'}">
    {#if badges}
      <div class="flex flex-wrap items-center gap-1.5">{@render badges()}</div>
    {/if}
    <!-- header: avatar + name / meta, divided from the content below -->
    <div class="flex items-center {framed ? 'border-b hair' : ''} {compact ? 'gap-2.5 pb-2.5' : 'gap-3 pb-3'}">
      {#if author?.member_id != null}
        <MemberHover memberId={author.member_id} class="relative z-10 shrink-0">
          <Avatar src={author?.picture_profile ?? null} name={authorName} size={compact ? 34 : 44} memberId={author.member_id} />
        </MemberHover>
      {:else}
        <Avatar src={author?.picture_profile ?? null} name={authorName} size={compact ? 34 : 44} />
      {/if}
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="redact truncate text-sm font-semibold text-base-content">{authorName}</span>
          {#if showHandle && author?.handle}<span class="truncate font-mono text-[0.62rem] text-base-content/58">@{author.handle}</span>{/if}
          <!-- On the author row rather than beside the title, because `title` is
               optional on a Skool post and this state must not depend on whether the
               post happens to have one. -->
          {#if deleted}<DeletedBadge kind="post" />{/if}
          {#if pinned}
            <span class="ml-auto inline-flex shrink-0 items-center gap-1 text-[0.66rem] font-medium text-primary" title="Pinned">
              <svg class="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M16 3l5 5-3 1-4 4 1 5-2 2-4-4-5 5-1-1 5-5-4-4 2-2 5 1 4-4z"/></svg>
              Pinned
            </span>
          {/if}
        </div>
        <div class="mt-0.5 flex items-center gap-1.5 text-[0.7rem] text-base-content/75">
          {#if createdAt}<span>{timeAgo(createdAt, now)}</span>{/if}
          {#if category}<span class="text-base-content/68">·</span><span class="truncate font-medium">{category}</span>{/if}
        </div>
      </div>
    </div>

    {#if mediaSide && hasMedia}
      <!-- Summary side layout: text left (governs height), media square on the right. -->
      <div class="flex items-start {compact ? 'gap-2.5' : 'gap-3'}">
        <div class="flex min-w-0 flex-1 flex-col {compact ? 'gap-2.5' : 'gap-3'}">{@render textBlock()}</div>
        <SkoolMedia images={mediaImages} {videos} {clamped} side {skoolUrl} interactive={!onclick} />
      </div>
    {:else}
      {@render textBlock()}
      {#if hasMedia}
        <SkoolMedia images={mediaImages} {videos} {clamped} {skoolUrl} interactive={!onclick} />
      {/if}
    {/if}

    <div class="flex items-center {compact ? 'gap-3' : 'gap-4'} text-[0.72rem] text-base-content/75">
      <Upvote count={upvotes} {liked} onLike={onLike} {compact} />
      {#if commentCount != null}
        <span class="inline-flex items-center gap-1">
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
          {commentCount}
        </span>
      {/if}
      {#if canReply}
        <button
          type="button"
          class="inline-flex items-center gap-1 font-medium transition-colors hover:text-primary {replyActive ? 'text-primary' : ''}"
          onclick={() => onReply?.()}
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>
          Reply
        </button>
      {/if}
      {#if skoolUrl || actions}
        <!-- relative z-10: sit above the card's stretched click layer so links/actions work. -->
        <div class="relative z-10 ml-auto flex items-center gap-3">
          {#if skoolUrl}
            <SkoolLink href={skoolUrl} />
          {/if}
          {#if actions}{@render actions()}{/if}
        </div>
      {/if}
    </div>

      </div>
{/snippet}

{#if onclick}
  <!-- Stretched-link card: a full-cover button (z-5) opens the thread; the avatar's
       MemberHover is raised above it (z-10) so its popup still works on hover. -->
  <div class="{baseCls} {stateCls} {deletedCls} relative cursor-pointer">
    {@render inner()}
    <button
      type="button"
      class="absolute inset-0 z-[5] rounded-box focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      aria-label="Open post"
      {onclick}
    ></button>
  </div>
{:else}
  <div class="{baseCls} {stateCls} {deletedCls}">{@render inner()}</div>
{/if}
